import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface PluxeeFormProps {
  onFormChange: (data: { gsm: string; otp: string }) => void;
  onValidityChange: (isValid: boolean) => void;
}

// Helper function to format the raw GSM number for display
const formatGsmForDisplay = (number: string): string => {
  const cleaned = number.replace(/\D/g, "");
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{2})(\d{2})$/);
  if (match) {
    return `${match[1]} ${match[2]} ${match[3]} ${match[4]}`;
  }
  return cleaned;
};

const PluxeeForm: React.FC<PluxeeFormProps> = ({
  onFormChange,
  onValidityChange,
}) => {
  const { t } = useTranslation();
  // gsm state still stores the raw 10-digit number
  const [gsm, setGsm] = useState("");
  const [otp, setOtp] = useState("");

  const pluxeeLogoUrl = window.themeVariables?.pluxeeLogoUrl;

  useEffect(() => {
    // Validation continues to use the raw 10-digit number
    const isGsmValid = /^[5][0-9]{9}$/.test(gsm);
    const isOtpValid = /^[0-9]{6}$/.test(otp);
    onValidityChange(isGsmValid && isOtpValid);
    // The raw 10-digit number is sent to the parent
    onFormChange({ gsm, otp });
  }, [gsm, otp, onFormChange, onValidityChange]);

  const handleGsmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Always clean the input to get raw digits before setting state
    const rawNumber = e.target.value.replace(/\D/g, "");
    setGsm(rawNumber.slice(0, 10));
  };

  return (
    <div className="payment-form-elements pluxee-form">
      {pluxeeLogoUrl && (
        <img
          src={pluxeeLogoUrl}
          alt="Pluxee Logo"
          className="pluxee-logo"
          width="150"
        />
      )}
      <p className="pluxee-disclaimer">
        {t("checkout.payment.pluxee_otp.disclaimer")}
      </p>
      <div className="form-field">
        <label htmlFor="pluxee-gsm">
          {t("checkout.payment.pluxee_otp.gsmLabel")}
        </label>
        {/* New wrapper for the input with prefix */}
        <div className="pluxee-gsm-input-wrapper">
          <span className="gsm-prefix">(+90)</span>
          <input
            type="tel"
            id="pluxee-gsm"
            // Display the formatted number
            value={formatGsmForDisplay(gsm)}
            // Handle change with our custom function
            onChange={handleGsmChange}
            placeholder={t("checkout.payment.pluxee_otp.gsmPlaceholder")}
            // Max length of formatted number is 13 (10 digits + 3 spaces)
            maxLength={13}
            autoComplete="tel-national"
          />
        </div>
      </div>
      <div className="form-field">
        <label htmlFor="pluxee-otp">
          {t("checkout.payment.pluxee_otp.otpLabel")}
        </label>
        <input
          type="text"
          id="pluxee-otp"
          inputMode="numeric"
          pattern="[0-9]*"
          value={otp}
          onChange={(e) =>
            setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
          }
          placeholder={t("checkout.payment.pluxee_otp.otpPlaceholder")}
          maxLength={6}
          autoComplete="one-time-code"
        />
      </div>
    </div>
  );
};

export default PluxeeForm;
